import React, { memo, PropsWithChildren, useEffect, useMemo, useState } from "react";
import { useLocation, useMatch } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import { logAnalyticsEvent } from "@/constants/amplitude";
import { Colors } from "@/constants/colors";
import { WebEventProps, WebEventTypes } from "@/constants/event";
import { ExperimentIds, ExperimentVariants } from "@/constants/experiments";
import { mapLocalizedLanguage } from "@/constants/languages";
import {
    CheckoutContainersIds,
    getPaymentSettings,
    preparePaymentContainers,
} from "@/constants/payment";
import { PricePlansIds } from "@/constants/prices";
import { Routes as AppRoutes, Routes } from "@/constants/routes";
import { useLocalization } from "@/hooks/useLocalization";
import { usePrefix } from "@/hooks/usePrefix";
import { useExperiment } from "@/utils/ExperimentsProvider";
import { useGender } from "@/utils/GenderProvider";
import { createPaymentClient, PaymentClient, PricePoint } from "@palta-brain/payments";
import { PaymentFlow } from "@primer-io/checkout-web";

import usePaymentSystem from "../hooks/usePaymentSystem";

import { useCurrency, useGeolocation } from "./GeolocationProvider";
import { localStorageManager, LocalStorageManagerKeys } from "./localStorageManager";
import useAnswers from "./useAnswers";
import useServer from "./useServer";

export interface PaymentsContextType {
    updatePaymentForm: () => () => void;
    initPaymentForm: () => () => void;
    paymentClient: PaymentClient | null;
    orderId: string | null;
    setPlanId: (planId: PricePlansIds | null) => void;
    planId: PricePlansIds | null;
    plan: PricePoint | null;
    pricePoints: PricePoint[];
    isPaymentFormInitialised: boolean;
    isPaymentsPending: boolean;
    isPaypalPaymentLoading: boolean;
    isPaymentComplete: boolean;
    isPaymentError: boolean;
    paymentMethod: string | null;
    isApplePayAvailable: boolean | null;
    setIsPaymentsPending: React.Dispatch<React.SetStateAction<boolean>>;
    setIsPaymentError: React.Dispatch<React.SetStateAction<boolean>>;
}

const defaultValues: PaymentsContextType = {
    orderId: null,
    planId: null,
    plan: null,
    paymentClient: null,
    pricePoints: [],
    isPaymentsPending: false,
    isPaypalPaymentLoading: false,
    isPaymentFormInitialised: false,
    isPaymentComplete: false,
    isPaymentError: false,
    paymentMethod: null,
    isApplePayAvailable: false,
    updatePaymentForm: () => () => null,
    initPaymentForm: () => () => null,
    setIsPaymentsPending: () => null,
    setIsPaymentError: () => null,
    setPlanId: () => null,
};

export const PaltaPaymentsV2Context = React.createContext<PaymentsContextType>(defaultValues);

const customerId = {
    type: "merchant-str",
    value: "",
};

let previousPrimerCheckout = Promise.resolve(null);

const PaywallNameByPathname: Record<string, string> = {
    [`/${Routes.NewYear}`]: "newyear24",
    [`/${Routes.PromoPaywall}`]: "promo",
};

const PaltaPaymentsV2Provider = ({ children }: PropsWithChildren) => {
    const { isLoaded: isCountryLoaded } = useCurrency();
    const { country } = useGeolocation();
    const { isFemale } = useGender();
    const { getEventType } = usePrefix();
    const { isApplePayAvailable } = usePaymentSystem();
    const [client, setClient] = useState<PaymentClient | null>(null);
    const [isPaymentFormInitialised, setIsPaymentFormInitialilised] = useState(false);
    const [pricePoints, setPricePoints] = useState<PricePoint[]>([]);
    const [orderId, setOrderId] = useState<string | null>(null);
    const [planId, setPlanId] = useState<PricePlansIds | null>(
        localStorageManager.getStoredItem<PricePlansIds>(LocalStorageManagerKeys.BOUGHT_PLAN_IDENT)
    );
    const [isPaymentComplete, setIsPaymentComplete] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState<string | null>(null);
    const [isPaypalPaymentLoading, setIsPaypalPaymentLoading] = useState(false);
    const [isPaymentsPending, setIsPaymentsPending] = useState(false);
    const [isPaymentError, setIsPaymentError] = useState(false);
    const { uuid } = useServer();

    const { variant: liftApplePayOnPaywallExpVariant } = useExperiment({
        experimentId: ExperimentIds.LIFT_APPLE_PAY_ON_PAYWALL,
    });

    const isFriendsFlow = useMatch({
        path: `/:both/${AppRoutes.Friends}`,
        end: false,
    });

    const isEmailFlow = useMatch({
        path: `/${AppRoutes.PromoPaywall}`,
        end: false,
    });

    const { email } = useAnswers();
    const { pathname } = useLocation();
    const { language } = useLocalization();

    const isDeeplinkScreen = localStorageManager.getStoredItem(
        LocalStorageManagerKeys.IS_AUTH_PASSED
    );

    const { isTest: isNewPaymentVersion, variant: newPaymentVersionVariant } = useExperiment({
        experimentId: ExperimentIds.NEW_PAYMENT_VERSION,
    });

    const { isTest: isLiftApplePayOnPaywallTest } = useExperiment({
        experimentId: ExperimentIds.LIFT_APPLE_PAY_ON_PAYWALL,
    });

    const isPaymentsInPopup = isLiftApplePayOnPaywallTest;

    const paywallName = useMemo(() => {
        const paywallType = PaywallNameByPathname[pathname];
        const paywallTypeSubstr = paywallType ? `${paywallType}_` : "";

        return pathname.match(Routes.NewYear)
            ? `${paywallTypeSubstr}paywall`
            : getEventType(`${paywallTypeSubstr}paywall`);
    }, [pathname, getEventType]);

    const plan = useMemo(
        () => pricePoints?.find((pp) => pp.ident === planId) || null,
        [planId, pricePoints]
    );

    useEffect(() => {
        if (uuid) {
            customerId.value = uuid;
        }
    }, [uuid]);

    const isCalisthenicsUrl = useMatch({
        path: `/${AppRoutes.Calisthenics}`,
        end: false,
    });

    const isOfferUrl = useMatch({
        path: `/${AppRoutes.Both}/${AppRoutes.Offer}`,
        end: false,
    });

    useEffect(() => {
        if (
            uuid &&
            isCountryLoaded &&
            !isDeeplinkScreen &&
            isApplePayAvailable !== null &&
            (newPaymentVersionVariant !== undefined ||
                isFriendsFlow ||
                isEmailFlow ||
                isCalisthenicsUrl ||
                isOfferUrl) &&
            !isNewPaymentVersion
        ) {
            initPaymentClient();
        }
    }, [
        uuid,
        isCountryLoaded,
        isDeeplinkScreen,
        isApplePayAvailable,
        isNewPaymentVersion,
        newPaymentVersionVariant,
    ]);

    const initPaymentClient = async () => {
        if (pricePoints.length) {
            return;
        }
        preparePaymentContainers(isApplePayAvailable);

        const settings = getPaymentSettings({
            isApplePayAvailable,
            onError: (error, description) => {
                console.log(description);
                console.log(error);
                logAnalyticsEvent(WebEventTypes.PURCHASE_FAILED, {
                    [WebEventProps.SCREEN]: paywallName,
                    error,
                    description,
                });
                setIsPaymentError(true);
            },
            onPaymentStatusChange: (status, method) => {
                //error status "FAIL" - когда сам праймер говорит что всё зафейлилось, "ERROR" - если на стороне Palta что-то упало
                console.log(status, method);
                if (method) {
                    setPaymentMethod(method);
                }
                if (status === "FAIL" || status === "ERROR") {
                    setIsPaymentsPending(false);
                    setIsPaypalPaymentLoading(false);
                }
                if (status === "PENDING") {
                    setIsPaymentsPending(true);
                    setIsPaymentError(false);
                    if (method === "PAYPAL") {
                        setIsPaypalPaymentLoading(true);
                    }
                    logAnalyticsEvent(WebEventTypes.PURCHASE_STARTED, {
                        [WebEventProps.SCREEN]: paywallName,
                        method,
                    });
                }
                if (status === "COMPLETE") {
                    setIsPaymentComplete(true);
                }
            },
        });

        const client = createPaymentClient(settings);

        setClient(client);

        await loadPricePoints(client);
    };

    const loadPricePoints = async (client: PaymentClient) => {
        logAnalyticsEvent(getEventType("prices_load_start"));
        const request = {
            customerId: customerId,
            availability_rules: true,
            ident: null,
            status: null,
            countryCode: country,
            platformCode: "desktop_web",
            requestContext: null,
        };

        const targetPricePoints = await client.getPricePoints(request);

        logAnalyticsEvent(getEventType("prices_loaded"));
        setPricePoints(targetPricePoints);

        return targetPricePoints;
    };

    const updatePaymentForm = () => {
        const orderId = uuidv4();
        setOrderId(orderId);

        return () => {
            if (!client || !planId) {
                return;
            }
            client?.updatePaymentForm({
                ident: planId,
                countryCode: country,
                platformCode: "desktop_web",
                orderId,
                customerId,
                customer: {
                    emailAddress: email,
                },
            });
        };
    };

    const isLightStyles = isFemale || isPaymentsInPopup;

    const initPaymentForm = () => {
        const orderId = uuidv4();
        setOrderId(orderId);

        return () => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            previousPrimerCheckout = previousPrimerCheckout.then(async (p) => {
                document
                    .querySelectorAll(".palta-payment-container")
                    .forEach((c) => (c.innerHTML = ""));

                planId &&
                    (await client?.showPaymentForm(
                        {
                            ident: planId,
                            countryCode: country,
                            platformCode: "desktop_web",
                            orderId,
                            customerId,
                            customer: {
                                emailAddress: email,
                            },
                        },
                        {
                            container: CheckoutContainersIds.Card,
                            locale: mapLocalizedLanguage[language],
                            vault: { visible: false },
                            card: {
                                cardholderName: {
                                    visible: false,
                                },
                            },
                            style: {
                                inputLabel: {
                                    fontFamily: "Outfit-Zing-VF",
                                    fontSize: "16px",
                                    color: isLightStyles ? "#797F91" : Colors.WHITE,
                                },
                                input: {
                                    base: {
                                        background: isLightStyles ? "#F3F4F7" : "transparent",
                                        height: 56,
                                        fontSize: "16px",
                                        lineHeight: "22px",
                                        borderStyle: "solid",
                                        borderColor: isLightStyles
                                            ? Colors.NEUTRAL_COOL_GREY_300
                                            : Colors.NEUTRAL_COOL_GREY_700,
                                        borderWidth: 1,
                                        borderRadius: 8,
                                        fontWeight: "400",
                                        color: isLightStyles ? "#030D1B" : Colors.WHITE,
                                        placeholder: {
                                            color: isLightStyles
                                                ? Colors.NEUTRAL_COOL_GREY_400
                                                : Colors.NEUTRAL_COOL_GREY_700,
                                            fontWeight: "400",
                                        },
                                        focus: {
                                            borderColor: isLightStyles
                                                ? Colors.WARM_TANGERINE_500
                                                : Colors.COOL_BLUE_500,
                                            background: "transparent",
                                        },
                                    },
                                },
                                submitButton: {
                                    base: {
                                        background: isLightStyles
                                            ? Colors.BLACK
                                            : Colors.COOL_BLUE_500,
                                        borderRadius: 16,
                                        fontSize: "16px",
                                        lineHeight: "20px",
                                        fontWeight: "600",
                                    },

                                    disabled: {
                                        //focus: TextStyle & BlockStyle,
                                    },

                                    loading: {
                                        //focus: TextStyle & BlockStyle,
                                    },
                                },
                                formSpacings: {
                                    betweenLabelAndInput: "4px",
                                    betweenInputs: "10px",
                                },
                            },
                            ...(isApplePayAvailable
                                ? {
                                      applePay: {
                                          container: CheckoutContainersIds.Apple,
                                          buttonType: "buy",
                                          buttonStyle:
                                              isLightStyles ||
                                              liftApplePayOnPaywallExpVariant ===
                                                  ExperimentVariants.TEST_1
                                                  ? "black"
                                                  : "white",
                                          captureBillingAddress: true,
                                      },
                                  }
                                : {}),
                            paypal: {
                                buttonColor: "gold",
                                buttonShape: "rect",
                                buttonSize: "large",
                                buttonLabel: "paypal",
                                paymentFlow: PaymentFlow.PREFER_VAULT,
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                // @ts-ignore
                                container: CheckoutContainersIds.PayPal,
                                buttonHeight: undefined,
                                onClick: () => {
                                    logAnalyticsEvent(WebEventTypes.WEB_BUTTON_CLICK, {
                                        [WebEventProps.BUTTON_NAME]: "paypal",
                                        [WebEventProps.METHOD]: "paypal",
                                        [WebEventProps.SCREEN]: paywallName,
                                    });
                                },
                            },
                            submitButton: {
                                // Callback for receiving the submit button's loading state in the current scene
                                onLoading(isLoading, context) {
                                    //'UniversalCheckout/Home'
                                    setIsPaymentsPending(isLoading);
                                    if (
                                        context.currentSceneId === "UniversalCheckout/Success/Check"
                                    ) {
                                        setIsPaymentsPending(true);
                                    }
                                },

                                // Callback for receiving the submit button's content in the current scene
                                onContentChange(content) {
                                    console.log("content context", content);
                                },
                            },
                            onPaymentMethodAction: (paymentMethodAction, { paymentMethodType }) => {
                                if (
                                    ["PAYMENT_METHOD_UNSELECTED"].includes(paymentMethodAction) &&
                                    paymentMethodType === "PAYPAL"
                                ) {
                                    setIsPaypalPaymentLoading(false);
                                }
                            },
                        }
                    ));
                setIsPaymentFormInitialilised(true);
            });
        };
    };

    return (
        <PaltaPaymentsV2Context.Provider
            value={{
                pricePoints,
                updatePaymentForm,
                initPaymentForm,
                setPlanId,
                isPaymentFormInitialised,
                paymentClient: client,
                orderId,
                planId,
                plan,
                isPaymentComplete,
                paymentMethod,
                isPaypalPaymentLoading,
                isApplePayAvailable,
                isPaymentsPending,
                setIsPaymentsPending,
                isPaymentError,
                setIsPaymentError,
            }}
        >
            {children}
        </PaltaPaymentsV2Context.Provider>
    );
};

export default memo(PaltaPaymentsV2Provider);
